import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { registerTransitionNotePrompt } from "@/lib/transitionNoteGate";

/**
 * Globally mounted "Note required" dialog (see AppProvider).
 *
 * A stage/status move that hits a "Notes" requirement is rejected by the
 * server with `noteRequired`; the axios interceptor holds that request's
 * promise, opens this dialog via `transitionNoteGate`, and retries the move
 * with the typed note. Cancelling surfaces the original error to the caller.
 */
export default function TransitionNoteHost() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [note, setNote] = useState("");
  const resolveRef = useRef<((note: string | null) => void) | null>(null);

  const settle = useCallback((value: string | null) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setOpen(false);
    setNote("");
  }, []);

  useEffect(() => {
    registerTransitionNotePrompt(
      (msg) =>
        new Promise<string | null>((resolve) => {
          resolveRef.current = resolve;
          setMessage(msg);
          setNote("");
          setOpen(true);
        })
    );
    return () => registerTransitionNotePrompt(null);
  }, []);

  const canSave = note.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && settle(null)}>
      <DialogContent className="sm:max-w-[440px] rounded-xl">
        <DialogHeader>
          <p className="text-base font-semibold text-[#051912]">Note required</p>
          <Separator />
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            {message || "A note is required to make this change."}
          </p>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Write a note…"
            className="min-h-[96px] border-gray-200"
            autoFocus
          />
        </div>

        <DialogFooter className="mt-2 flex justify-end gap-2">
          <Button variant="cancel" onClick={() => settle(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSave}
            onClick={() => settle(note.trim())}
          >
            Save note & continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
